import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Skeleton } from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Groups as GroupsIcon,
  Domain as DomainIcon,
  CalendarMonth as CalendarIcon,
  SupervisorAccount as LeaderIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { OrganizationStats } from '@/services/OrganizationSetupService';

interface StatsCardsProps {
  stats: OrganizationStats | null;
  loading: boolean;
}

interface StatCardProps {
  title: string;
  value: number;
  icon: React.ReactNode;
  color: string;
  suffix?: string;
  loading: boolean;
}

// Animated counter hook
const useAnimatedCounter = (target: number, duration: number = 1000) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (target === 0) {
      setCount(0);
      return;
    }

    let startTime: number;
    let animationFrame: number;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);

      // Easing function for smooth animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(easeOutQuart * target));

      if (progress < 1) {
        animationFrame = requestAnimationFrame(animate);
      }
    };

    animationFrame = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(animationFrame);
  }, [target, duration]);

  return count;
};

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, color, suffix, loading }) => {
  const theme = useTheme();
  const animatedValue = useAnimatedCounter(value);

  return (
    <Card
      elevation={0}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
          borderColor: color
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 1, fontWeight: 500 }}
            >
              {title}
            </Typography>
            {loading ? (
              <Skeleton variant="text" width={80} height={48} />
            ) : (
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: color,
                  lineHeight: 1
                }}
              >
                {animatedValue}
                {suffix && (
                  <Typography
                    component="span"
                    variant="h5"
                    sx={{ fontWeight: 500, ml: 0.5 }}
                  >
                    {suffix}
                  </Typography>
                )}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              backgroundColor: `${color}15`,
              color: color
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const StatsCards: React.FC<StatsCardsProps> = ({ stats, loading }) => {
  const theme = useTheme();

  const statItems = [
    {
      title: 'Total Employees',
      value: stats?.total_employees || 0,
      icon: <GroupsIcon sx={{ fontSize: 28 }} />,
      color: theme.palette.primary.main
    },
    {
      title: 'Teams',
      value: stats?.total_departments || 0,
      icon: <DomainIcon sx={{ fontSize: 28 }} />,
      color: '#43A047'
    },
    {
      title: 'Years in Business',
      value: stats?.years_in_business || 0,
      icon: <CalendarIcon sx={{ fontSize: 28 }} />,
      color: '#FF9800',
      suffix: stats?.years_in_business === 1 ? 'yr' : 'yrs'
    },
    {
      title: 'Leadership Team',
      value: stats?.leadership_count || 0,
      icon: <LeaderIcon sx={{ fontSize: 28 }} />,
      color: '#7B1FA2'
    }
  ];

  return (
    <Grid container spacing={3}>
      {statItems.map((item, index) => (
        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={index}>
          <StatCard
            title={item.title}
            value={item.value}
            icon={item.icon}
            color={item.color}
            suffix={item.suffix}
            loading={loading}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default StatsCards;
